import { notification } from 'antd'
import { useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from '@api'
import { useGeneral, useHandleNewTicketModal } from '@contexts'
import { AxiosErrorType } from '@types'

import { TicketsApi } from './api'

const useCreateTicket = () => {
  const { t } = useTranslation()
  const tPreffix = 'suppliers_router.suppliers_dashboard.use_create_ticket.'
  const { setIsNewTicketModalOpen } = useHandleNewTicketModal()
  const { formInstance } = useGeneral()
  const queryClient = useQueryClient()

  const onCreateSuccess = () => {
    setIsNewTicketModalOpen(false)
    queryClient.invalidateQueries(QUERY_KEYS.TICKETS)
    queryClient.invalidateQueries(QUERY_KEYS.TICKETS_COUNT)
    queryClient.invalidateQueries(QUERY_KEYS.TO_AUTHORIZE)
    queryClient.invalidateQueries(QUERY_KEYS.MATERIALS)
  }

  const { mutate, isLoading } = useMutation(TicketsApi.createTicket, {
    onSuccess: data => {
      notification.success({
        message: [
          t(`${tPreffix}new_ticket_notification`),
          `${data.tickets.map(ticket => ` ${ticket.id}`)}`,
        ],
      })
      onCreateSuccess()
    },
    onError: (err: unknown) => {
      const error = err as AxiosErrorType

      const errorResponse = error.data.error as {
        type: string
        status: number
        message: string
      }

      if (errorResponse.type === 'NO_ACTIVE_ROUTE') {
        const hint =
          errorResponse.message.length > 62
            ? `${t(`${tPreffix}no_active_route_hint`)} ${errorResponse.message.substring(
                62,
                errorResponse.message.length,
              )}`
            : `${t(`${tPreffix}no_active_route_at_all_hint`)}`

        notification.error({
          message: `${t(`${tPreffix}no_active_route`)} ${hint}`,
        })
      } else if (errorResponse.type === 'TICKET_SAP_ALREADY_EXISTS') {
        notification.error({
          message: t(
            'suppliers_router.suppliers_dashboard.use_create_ticket.ticket_sap_already_exists',
          ),
        })
        //TODO: código agregado para que el error se muestre en el modal de creación de ticket
        formInstance?.setFields([
          {
            name: 'ticketSap',
            errors: [
              t('suppliers_router.suppliers_dashboard.use_create_ticket.ticket_sap_already_exists'),
            ],
          },
        ])
      }
      if (errorResponse.type === 'TICKET_SAP_PO_BLOCKED') {
        notification.error({
          message: t(
            'suppliers_router.suppliers_dashboard.use_create_ticket.ticket_sap_po_blocked',
          ),
        })
        queryClient.invalidateQueries(QUERY_KEYS.MATERIALS)
      } else {
        notification.error({
          message: t(`${tPreffix}error`),
        })
      }
    },
  })

  return {
    createTicket: mutate,
    isLoading,
  }
}

export { useCreateTicket }
